import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, DataTable, EmptyState } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, LifeBuoy, Plus, MessageSquare } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";
import { useLocalStorageState } from "@/hooks/useLocalStorageState";

const PAGE_SIZE = 50;

export const SUPPORT_STATUS_STYLES = {
  OPEN: "bg-blue-50 text-blue-700 border-blue-200",
  IN_PROGRESS: "bg-amber-50 text-amber-700 border-amber-200",
  AWAITING_REPLY: "bg-purple-50 text-purple-700 border-purple-200",
  RESOLVED: "bg-green-50 text-green-700 border-green-200",
  CLOSED: "bg-slate-100 text-slate-600 border-slate-200",
};
export const SUPPORT_STATUS_LABELS = {
  OPEN: "Open", IN_PROGRESS: "In Progress", AWAITING_REPLY: "Awaiting Reply", RESOLVED: "Resolved", CLOSED: "Closed",
};
export const SUPPORT_PRIORITY_STYLES = {
  low: "text-slate-500", medium: "text-blue-600", high: "text-amber-600", urgent: "text-red-600",
};
export const SUPPORT_CATEGORY_LABELS = {
  orders: "Order / Shipment", pickup: "Pickup", billing: "Billing / Wallet",
  kyc: "KYC / Onboarding", returns_ndr: "Returns / NDR", tech: "Technical", other: "Other",
};

const TABS = [
  { id: "all", label: "All" },
  { id: "OPEN", label: "Open" },
  { id: "IN_PROGRESS", label: "In Progress" },
  { id: "AWAITING_REPLY", label: "Awaiting Reply" },
  { id: "RESOLVED", label: "Resolved" },
  { id: "CLOSED", label: "Closed" },
];

export const StatusPill = ({ status }) => (
  <Badge variant="outline" className={SUPPORT_STATUS_STYLES[status] || "border-slate-200"}>
    {SUPPORT_STATUS_LABELS[status] || status}
  </Badge>
);

const errDetail = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.response?.data?.message || fb;
};

const Support = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const canCreate = can(user, "support:create");

  const [activeTab, setActiveTab] = useLocalStorageState("seller-support:tab", "all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (activeTab !== "all") params.status = activeTab;
      if (search) params.search = search;
      const { data: res } = await axios.get(`${API}/support/tickets`, { params });
      setData({ items: res.items || [], total: res.total || 0, counts: res.counts || {} });
    } catch (e) {
      toast.error(errDetail(e, "Failed to load tickets"));
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, search]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { setPage(1); }, [activeTab, search]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const tabCount = (id) => (id === "all" ? data.counts.all : data.counts[id]);

  const columns = [
    { key: "ticket", header: "Ticket", primary: true,
      cell: (r) => (
        <button onClick={() => navigate(`/seller/support/${r.id}`)} className="text-left">
          <span className="text-sm font-medium text-brand hover:underline">{r.ticket_number}</span>
          <div className="text-sm text-slate-800 line-clamp-1 max-w-[280px]">{r.subject}</div>
        </button>
      ) },
    { key: "category", header: "Category", hideOnMobile: true,
      cell: (r) => <span className="text-sm text-slate-600">{SUPPORT_CATEGORY_LABELS[r.category] || r.category}</span> },
    { key: "priority", header: "Priority", hideOnMobile: true,
      cell: (r) => <span className={`text-sm font-medium capitalize ${SUPPORT_PRIORITY_STYLES[r.priority] || ""}`}>{r.priority}</span> },
    { key: "status", header: "Status", cell: (r) => <StatusPill status={r.status} /> },
    { key: "activity", header: "Last activity", hideOnMobile: true,
      cell: (r) => (
        <div className="text-xs text-slate-500">
          <div>{formatToIST(r.last_reply_at || r.updated_at)}</div>
          {r.last_reply_by && <div className="text-slate-400">{r.last_reply_by === "admin" ? "Support replied" : "You replied"}</div>}
        </div>
      ) },
    { key: "msgs", header: "", hideOnMobile: true,
      cell: (r) => <span className="inline-flex items-center gap-1 text-xs text-slate-400"><MessageSquare className="w-3.5 h-3.5" />{r.message_count}</span> },
  ];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 w-full">
        <PageHeader
          title="Support"
          subtitle="Raise a ticket and track the conversation with our support team"
          actions={canCreate ? <Button onClick={() => navigate("/seller/support/create")}><Plus className="w-4 h-4 mr-1.5" /> Raise ticket</Button> : null}
        />

        <div className="flex gap-2 border-b border-slate-200 bg-white px-2 sm:px-6 rounded-t-lg shadow-sm overflow-x-auto">
          {TABS.map((tab) => {
            const c = tabCount(tab.id);
            return (
              <button key={tab.id} type="button" onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id ? "border-brand text-brand" : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                }`}>
                {tab.label}{c != null && <span className="font-bold text-brand"> ({c})</span>}
              </button>
            );
          })}
        </div>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <Input placeholder="Search by ticket #, subject, order # or AWB..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="pl-9" />
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={data.items}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/seller/support/${r.id}`)}
          loading={loading}
          empty={<EmptyState icon={LifeBuoy} title="No tickets yet" description="Raise a ticket and our support team will help you out." action={canCreate ? <Button onClick={() => navigate("/seller/support/create")}><Plus className="w-4 h-4 mr-1.5" /> Raise ticket</Button> : undefined} />}
          pagination={totalPages > 1 ? { page, pages: totalPages, total: data.total, pageSize: PAGE_SIZE, onPageChange: setPage } : undefined}
        />
      </div>
    </SellerLayout>
  );
};

export default Support;
